import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, from } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Kno2DocumentSource } from '../model/kno2-document-source';
import { Cacheable } from 'ts-cacheable';
import { Kno2DirectorySearch } from '../model/kno2-directory-search';

@Injectable({
  providedIn: 'root'
})
export class Kno2Service
{
  public providers: Observable<any>;
  public organizations: Observable<any>;

  private sourceProviders: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private sourceOrganizations: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private providerPageStart = 1;
  private organizationPageStart = 1;
  private providersData: any[] = [];
  private organizationsData: any[] = [];
  private completed: boolean[] = [];
  private firstSearch: boolean;

  constructor(private http: HttpClient)
  {
    this.providers = this.sourceProviders.asObservable();
    this.organizations = this.sourceOrganizations.asObservable();
  }

  /**
   * Get Kno2 document sources from cache.
   */
  @Cacheable()
  public getCachedDocumentSources(): Observable<Kno2DocumentSource[]>
  {
    return this.http.get<Kno2DocumentSource[]>('/kno2/documentsources');
  }

  /**
   * Reset previous search results.
   */
  public searchDirectoryReset(): void
  {
    this.firstSearch = true;
    this.completed[0] = false;  // Providers
    this.completed[1] = false;  // Organizations
    this.providerPageStart = 1;
    this.organizationPageStart = 1;
    this.providersData = [];
    this.organizationsData = [];
    this.sourceProviders.next(this.providersData);
    this.sourceOrganizations.next(this.organizationsData);
  }

  /**
   * Searching Kno2 directory.
   *
   * @param tabIndex Indicates which tab index we are currently rendering. This is required to render the providers or organizations tab.
   * @param pageSize Indicates the page size required.
   * @param search Search criteria.
   */
  public searchDirectory(tabIndex: number, pageSize: number, search: Kno2DirectorySearch): Observable<any>
  {
    if (this.completed[tabIndex])
    {
      return from([true]);
    }

    // Set Kno2 query by setting page start and page size
    search.providerPageStart = this.providerPageStart;
    search.organizationPageStart = this.organizationPageStart;
    search.pageSize = pageSize;

    const progress = this.http
      .post(`/kno2/directory/search`, search)
      .pipe(
        map(response => response),
        tap((values: any) =>
        {
          if (values.providers.length === 0 && tabIndex === 0 && !this.firstSearch)
          {
            this.completed[tabIndex] = true;
          }
          else
            if (values.organizations.length === 0 && tabIndex === 1 && !this.firstSearch)
            {
              this.completed[tabIndex] = true;
            }
            else
            {
              // On first search, both dataset must be updated
              if (tabIndex === 0 || this.firstSearch)
              {
                this.providersData = [...this.providersData, ...values.providers];
                this.sourceProviders.next(this.providersData);
              }

              if (tabIndex === 1 || this.firstSearch)
              {
                this.organizationsData = [...this.organizationsData, ...values.organizations];
                this.sourceOrganizations.next(this.organizationsData);
              }

              // On the first search must increase the pagestart for the next lookup (if requested)
              if (this.firstSearch)
              {
                this.providerPageStart = this.providerPageStart + 1;
                this.organizationPageStart = this.organizationPageStart + 1;
              }
              else
              {
                // Increase the PageStart depending on the selected tab.
                switch (tabIndex)
                {
                  case 0:
                    this.providerPageStart = this.providerPageStart + 1;
                    break;
                  case 1:
                    this.organizationPageStart = this.organizationPageStart + 1;
                    break;
                }
              }
            }
          this.firstSearch = false;
        })
      );
    return progress;
  }

  /**
   * Get Kno2 document sources.
   */
  public getDocumentSources(): Observable<Kno2DocumentSource[]>
  {
    return this.http.get<Kno2DocumentSource[]>('/kno2/documentsources');
  }
}
